/**
 * Heap.io analytics integration
 * @see https://developers.heap.io/reference/api-reference-overview
 */

import * as Sentry from '@sentry/vue'

import IDENTITY from '@/queries/Identity.gql'

// Store a reference to the Apollo client for internal use
let apolloClientInstance = null
let currentHeapUser = null
let isHeapLoaded = false

// Heap does not support re-initializing with a different environment ID after
// the SDK has loaded (https://community.heap.io/using-the-product-46/point-to-different-heap-project-runtime-within-the-app-435,
// unresolved feature request). So the US environment can only be selected
// *before* the first heap.load() call, meaning we need to know the market
// upfront, in priority order:
// - `?market=US` on the URL (e.g. a manually tagged invite link) - explicit override
// - the market already CONFIRMED for this browser by a previous login (see
//   setHeapMarketForNextLoad) - stored as 'US' or 'OTHER', deliberately
//   distinct from "never confirmed" so a known non-US account that happens to
//   browse from a US IP later (travel/VPN) isn't reclassified by the cookie
//   below.
// - only if NEITHER of the above exists yet (never logged in on this browser
//   before): a `market=US` cookie set by Cloudflare from the visitor's IP
//   (see infra docs) - acceptable for now since there is no established
//   non-US customer base that this could misclassify; revisit if/when that
//   changes.
// - failing all of that, the registration form (/auth/register) still loads
//   the general environment immediately - there's no company yet to know a
//   market for, and delaying here would drop sign-up funnel tracking entirely.
// - everywhere else (login, deep links into the app), Heap loading is
//   deferred until the Company.country query resolves (see
//   setHeapMarketForNextLoad), since by then a companyId - and usually its
//   country - already exists; the delay is one GraphQL round-trip.
const HEAP_MARKET_STORAGE_KEY = 'heapMarket'

function getQueryMarket() {
  return new URLSearchParams(window.location.search).get('market')?.toUpperCase() || null
}

function getConfirmedMarket() {
  try {
    return localStorage.getItem(HEAP_MARKET_STORAGE_KEY)
  } catch {
    return null
  }
}

function getCloudflareCookieMarket() {
  try {
    return document.cookie.match(/(?:^|;\s*)market=([^;]+)/)?.[1]?.toUpperCase() || null
  } catch {
    return null
  }
}

// Captured before any mutation, so a mistaken market signal (typo'd query
// param, stale cookie) can be reverted to the general environment as long as
// heap.load() hasn't fired yet.
const GENERAL_HEAP_ENV_ID = window.HEAP_ENV_ID

function applyUsEnvIdIfPossible() {
  if (import.meta.env.VITE_APP_HEAP_ID_US) {
    window.HEAP_ENV_ID = import.meta.env.VITE_APP_HEAP_ID_US
  }
}

const queryMarket = getQueryMarket()
const confirmedMarket = getConfirmedMarket()
// Only consult the IP-based cookie if this browser has never been through an
// actual login before - a real login's result always takes precedence.
const cloudflareMarket = confirmedMarket === null ? getCloudflareCookieMarket() : null

let knownMarket = queryMarket || confirmedMarket || cloudflareMarket

try {
  if (queryMarket === 'US' && confirmedMarket === null) {
    localStorage.setItem(HEAP_MARKET_STORAGE_KEY, 'US')
  }
} catch {
  // localStorage may be unavailable (e.g. private browsing) - the query param
  // still applies to this session, just won't be remembered for the next one
}

if (window.HEAP_ENV_ID && knownMarket === 'US') {
  applyUsEnvIdIfPossible()
}

const isRegisterRoute = window.location.pathname.startsWith('/auth/register')

// Whether we already know which environment to use, i.e. heap.load() may
// proceed as soon as consent allows it.
let marketDecided = isRegisterRoute || knownMarket !== null
// If consent arrives before the market is decided, remember to load once it is.
let pendingMeasurementConsent = null

// Marks the market as decided (heap.load() may proceed) and retries the
// consent sync if it was waiting on this. No-op if already decided - so
// setHeapMarketForNextLoad winning the race against the fallback timeout
// below (or vice versa) is safe either way.
function decideMarket() {
  if (marketDecided) {
    return
  }
  marketDecided = true
  if (pendingMeasurementConsent) {
    pendingMeasurementConsent = null
    syncHeapTrackingConsent(true)
  }
}

// There are many public routes that never authenticate at all (marketing/
// utility pages, health checks, etc.) - enumerating all of them here would be
// one more place to keep in sync with the router. Instead: if nothing has
// resolved the market within a couple seconds, assume this isn't an
// authenticated flow and fall back to the general environment (already the
// default in window.HEAP_ENV_ID unless a query param/cache said otherwise).
// A real login normally resolves Company.country well within this window.
if (!marketDecided) {
  setTimeout(decideMarket, 2000)
}

/**
 * Sets the Apollo client to be used for GraphQL queries
 * This should be called during app initialization
 * @param {Object} client - Apollo client instance
 */
export function setApolloClient(client) {
  apolloClientInstance = client
}

function hasHeapMethod(methodName) {
  return window.heap && typeof window.heap[methodName] === 'function'
}

function getHeapUserId(user) {
  return user?.userId || user?.sub?.split('|')?.[1] || null
}

/**
 * Synchronizes Heap tracking with the current measurement consent state.
 * Loads Heap on first consent, resumes it after a prior stop, and stops it
 * when consent is withdrawn.
 * @param {boolean} hasMeasurementConsent
 */
export function syncHeapTrackingConsent(hasMeasurementConsent) {
  if (!window.heap || !window.HEAP_ENV_ID) {
    return
  }

  try {
    if (!hasMeasurementConsent) {
      pendingMeasurementConsent = null
      if (hasHeapMethod('stopTracking')) {
        window.heap.stopTracking()
      }
      return
    }

    if (!marketDecided) {
      // Market not known yet (not the registration route, no query param/cache) -
      // wait for setHeapMarketForNextLoad to decide, then retry.
      pendingMeasurementConsent = true
      return
    }

    if (!isHeapLoaded && hasHeapMethod('load')) {
      window.heap.load(window.HEAP_ENV_ID)
      isHeapLoaded = true
      flushPendingReliableHeapEvents()
    } else if (hasHeapMethod('startTracking')) {
      window.heap.startTracking()
    }

    if (currentHeapUser) {
      identifyHeapUser(currentHeapUser)
    }
  } catch (error) {
    console.error('Failed to synchronize Heap.io tracking:', error)
  }
}

/**
 * Identifies a user in Heap.io with additional data from the Identity GraphQL query
 * @param {Object} user - The user object from authentication
 * @param {string} user.userId - The user's ID
 * @param {string} user.sub - The Auth0 subject (fallback for user ID)
 * @param {string} user.email - The user's email
 * @param {string} user.companyId - The company ID
 * @param {string} [user.name] - The user's name
 * @param {string} [user.firstName] - The user's first name
 * @param {string} [user.lastName] - The user's last name
 */
export function identifyHeapUser(user) {
  if (!window.heap || typeof window.heap.identify !== 'function') {
    return
  }

  if (!user) {
    return
  }

  const userId = getHeapUserId(user)

  if (!userId) {
    return
  }

  // getTokenSilently() re-invokes this on every GraphQL request (see
  // vue-apollo.js's auth link), so without this guard the same user gets
  // re-identified - and Identity re-fetched/re-pushed - on every single API
  // call instead of once per session.
  if (currentHeapUser && getHeapUserId(currentHeapUser) === userId) {
    return
  }

  try {
    currentHeapUser = user

    // Identify the user with their ID (do this immediately)
    window.heap.identify(userId)

    // Fetch and add all properties together
    _addAllUserProperties(user).catch((error) => {
      console.error('Failed to add user properties to Heap.io:', error)
    })
  } catch (error) {
    console.error('Failed to identify user in Heap.io:', error)
  }
}

/**
 * Fetches Identity data and adds all user properties to Heap.io in a single call.
 * Uses cache-first policy to avoid unnecessary requests.
 * @param {Object} user - The user object from authentication
 * @private
 */
async function _addAllUserProperties(user) {
  if (!window.heap || typeof window.heap.addUserProperties !== 'function') {
    return
  }

  // Start with properties from auth user object
  const authProperties = {
    ...(user.email && { email: user.email }),
    ...(user.companyId && { companyId: user.companyId }),
    ...(user.name && { name: user.name }),
    ...(user.firstName && { firstname: user.firstName }),
    ...(user.lastName && { lastname: user.lastName }),
  }

  let identityProperties = {}

  // Fetch additional properties from Identity query if Apollo client is available
  if (apolloClientInstance) {
    try {
      const { data } = await apolloClientInstance.query({
        query: IDENTITY,
        fetchPolicy: 'cache-first', // Use cached data if available
      })

      if (data?.identity) {
        const { user: identityUser, company } = data.identity

        identityProperties = {
          // User properties from Identity query
          ...(identityUser?.firstname && { firstname: identityUser.firstname }),
          ...(identityUser?.lastname && { lastname: identityUser.lastname }),
          ...(identityUser?.role?.name && { roleName: identityUser.role.name }),
          ...(identityUser?.role?.slug && { roleSlug: identityUser.role.slug }),
          ...(identityUser?.appLocale && { appLocale: identityUser.appLocale }),
          ...(identityUser?.createdAt && { userCreatedAt: identityUser.createdAt }),
          ...(typeof identityUser?.isContractOwner === 'boolean' && {
            isContractOwner: identityUser.isContractOwner,
          }),
          // Company properties from Identity query
          ...(company?.name && { companyName: company.name }),
          ...(company?.slug && { companySlug: company.slug }),
          ...(company?.country && { companyCountry: company.country }),
          ...(company?.productType && { companyProductType: company.productType }),
          ...(company?.platform && { companyPlatform: company.platform }),
          ...(company?.mailProvider && { companyMailProvider: company.mailProvider }),
        }
      }
    } catch (error) {
      // Continue with auth properties only if Identity fetch fails
    }
  }

  // Merge auth and identity properties (identity takes precedence)
  const allProperties = { ...authProperties, ...identityProperties }

  // Add all properties in a single call
  if (Object.keys(allProperties).length > 0) {
    window.heap.addUserProperties(allProperties)
  }
}

/**
 * Records the account's market (Company.country, not locale/IP-geo - see
 * marketTracking.js for why), called once Company.country resolves (whatever
 * it is, including non-US). If this session's heap.load() was deferred
 * because the market wasn't decided yet (see marketDecided above), this is
 * what unblocks it: sets the environment for *this* load (skipped only if
 * Heap already loaded via the registration bypass or a query param), and
 * retries syncHeapTrackingConsent if consent was already granted while we
 * were waiting. Cannot switch an already-loaded session (Heap has no
 * supported API for that, see the note above) - a US company reached via the
 * registration bypass stays in the general environment for that one session.
 * @param {string} country - Company.country, e.g. 'us', 'de'
 */
export function setHeapMarketForNextLoad(country) {
  const usEnvId = import.meta.env.VITE_APP_HEAP_ID_US
  const isUsMarket = country?.toUpperCase() === 'US'

  try {
    if (isUsMarket && usEnvId) {
      localStorage.setItem(HEAP_MARKET_STORAGE_KEY, 'US')
      if (!isHeapLoaded) {
        window.HEAP_ENV_ID = usEnvId
      }
    } else {
      // Explicitly confirmed non-US (not just "unset") - takes precedence over
      // the Cloudflare cookie on this browser's future visits.
      localStorage.setItem(HEAP_MARKET_STORAGE_KEY, 'OTHER')
      if (!isHeapLoaded) {
        window.HEAP_ENV_ID = GENERAL_HEAP_ENV_ID
      }
    }
  } catch {
    // localStorage may be unavailable (e.g. private browsing) - Heap keeps using
    // whichever environment is already set
  }

  decideMarket()
}

/**
 * Tracks a custom event in Heap.io.
 * @see https://developers.heap.io/reference/track-1
 * @param {string} name - The event name
 * @param {Object} [properties] - Optional event properties
 */
export function trackHeapEvent(name, properties) {
  if (!isHeapLoaded || !hasHeapMethod('track')) {
    Sentry.captureMessage('Heap event dropped: not loaded', {
      tags: { category: 'tracking' },
      extra: { name, properties },
    })
    return
  }

  try {
    window.heap.track(name, properties)
  } catch (error) {
    Sentry.captureException(error, {
      tags: { category: 'tracking' },
      extra: { name, properties },
    })
    console.error(`Failed to track Heap.io event "${name}":`, error)
  }
}

// Events fired via trackHeapEventReliably before Heap has finished loading
// are held here instead of being dropped,
// and flushed the moment isHeapLoaded flips true.
const pendingReliableHeapEvents = []

function flushPendingReliableHeapEvents() {
  if (!hasHeapMethod('track')) {
    return
  }
  const events = pendingReliableHeapEvents.splice(0, pendingReliableHeapEvents.length)
  for (const { name, properties, onAccepted, onFailure } of events) {
    try {
      window.heap.track(name, properties)
      onAccepted?.()
    } catch (error) {
      Sentry.captureException(error, {
        tags: { category: 'tracking' },
        extra: { name, properties },
      })
      console.error(`Failed to track Heap.io event "${name}":`, error)
      onFailure?.(error)
    }
  }
}

/**
 * * Like trackHeapEvent, but does not drop the event when Heap hasn't loaded
 * yet: it's queued and the SDK call is made automatically once loading
 * completes (see flushPendingReliableHeapEvents, called from
 * syncHeapTrackingConsent). Use this for anything that persists "this
 * milestone was tracked" state - only persist that state inside onAccepted,
 * never before calling this.
 *
 * Note: onAccepted fires once window.heap.track() has been called, not once
 * Heap's backend has confirmed receipt - the client SDK is fire-and-forget
 * and gives no delivery acknowledgment.
 * @param {string} name
 * @param {Object} [properties]
 * @param {() => void} [onAccepted] - called once window.heap.track() has been invoked
 * @param {(error: unknown) => void} [onFailure] - called if that call throws
 */
export function trackHeapEventReliably(name, properties, onAccepted, onFailure) {
  if (isHeapLoaded && hasHeapMethod('track')) {
    try {
      window.heap.track(name, properties)
      onAccepted?.()
    } catch (error) {
      Sentry.captureException(error, {
        tags: { category: 'tracking' },
        extra: { name, properties },
      })
      console.error(`Failed to track Heap.io event "${name}":`, error)
      onFailure?.(error)
    }
    return
  }
  pendingReliableHeapEvents.push({ name, properties, onAccepted, onFailure })
}

/**
 * Resets the user identity in Heap.io (called on logout)
 */
export function resetHeapIdentity() {
  if (!window.heap || typeof window.heap.resetIdentity !== 'function') {
    return
  }

  try {
    currentHeapUser = null
    window.heap.resetIdentity()
    // Clear the cached market so a different account logging in on this same
    // browser (shared machine) doesn't inherit a stale US routing decision.
    localStorage.removeItem(HEAP_MARKET_STORAGE_KEY)
  } catch (error) {
    console.error('Failed to reset Heap.io identity:', error)
  }
}
